import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MapPin,
  Calendar,
  User,
  FileText,
  Loader2,
  Search,
  ClipboardCheck,
  ClipboardList,
  MoreVertical,
  Trash2,
  Eye,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

// Determine remediation issues for a visit
function computeIssues(v) {
  const failMeasures = [];
  let failCount = 0;
  (v.measures || [])
    .filter((m) => m.installed)
    .forEach((m) => {
      const fails = Object.values(m.answers || {}).filter((a) => a === false).length;
      if (fails > 0) {
        failCount += fails;
        failMeasures.push(m.code);
      }
    });
  const openFaults = (v.actions || []).filter((a) => (a.status || "Open") !== "Closed").length;
  return { failCount, openFaults, failMeasures: [...new Set(failMeasures)], hasIssue: failCount > 0 || openFaults > 0 };
}

function VisitCard({ v, issues, onDelete, navigate, isAdmin }) {
  const installed = (v.measures || []).filter((m) => m.installed);
  return (
    <Card
      className={`group flex flex-col p-5 shadow-sm transition-all ${issues.hasIssue ? "border-2 border-amber-300 hover:border-amber-400" : "border-slate-200 hover:border-slate-400"}`}
      data-testid={`visit-card-${v.id}`}
    >
      <div className="flex items-start justify-between">
        <Badge className={`font-bold uppercase ${v.visit_type === "mid" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" : "bg-accent/15 text-accent hover:bg-accent/15"}`}>
          {v.visit_type === "mid" ? "Mid Coordination" : "Post Coordination"}
        </Badge>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={v.status === "completed" ? "border-accent text-accent" : "border-slate-300 text-slate-500"}>
            {v.status === "completed" ? "Completed" : "Draft"}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`visit-menu-${v.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/visits/${v.id}`)}><Eye className="mr-2 h-4 w-4" /> View</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/visits/${v.id}/edit`)}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(v.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Link to={`/visits/${v.id}`} className="mt-4 flex-1">
        <h3 className="font-heading text-lg font-bold leading-snug text-slate-900">{v.job_address || "No address"}</h3>
        <div className="mt-3 space-y-1.5 text-sm text-slate-500">
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {v.postcode || "—"} · {v.client_scheme}</div>
          <div className="flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> {v.completed_by}</div>
          <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> {v.visit_date}</div>
        </div>
      </Link>

      {isAdmin && v.created_by_name && (
        <div className="mt-2 inline-flex w-fit items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600" data-testid={`visit-owner-${v.id}`}>
          <User className="h-3 w-3" /> {v.created_by_name}
        </div>
      )}

      {issues.hasIssue && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2" data-testid={`remediation-badge-${v.id}`}>
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            {issues.failCount > 0 && <span>{issues.failCount} failed check{issues.failCount > 1 ? "s" : ""}</span>}
            {issues.failCount > 0 && issues.openFaults > 0 && <span>·</span>}
            {issues.openFaults > 0 && <span>{issues.openFaults} open fault{issues.openFaults > 1 ? "s" : ""}</span>}
          </div>
          {issues.failMeasures.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {issues.failMeasures.map((c) => (
                <span key={c} className="rounded bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">{c}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
        {installed.length === 0 ? (
          <span className="text-xs text-slate-400">No measures marked installed</span>
        ) : (
          installed.map((m) => (
            <span key={m.measure_id} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{m.code}</span>
          ))
        )}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [visits, setVisits] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/visits").then((r) => setVisits(r.data));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this site visit? This cannot be undone.")) return;
    await api.delete(`/visits/${id}`);
    toast.success("Visit deleted");
    load();
  };

  if (!visits)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent" />
      </div>
    );

  const withIssues = visits.map((v) => ({ v, issues: computeIssues(v) }));

  const filtered = withIssues
    .filter(({ v }) => (filter === "all" ? true : v.visit_type === filter))
    .filter(({ v }) => {
      const s = q.toLowerCase();
      return !s || (v.job_address || "").toLowerCase().includes(s) || (v.postcode || "").toLowerCase().includes(s) || (v.completed_by || "").toLowerCase().includes(s);
    });

  const remediation = filtered.filter((x) => x.issues.hasIssue);
  const clear = filtered.filter((x) => !x.issues.hasIssue);

  const stats = {
    total: visits.length,
    remediation: withIssues.filter((x) => x.issues.hasIssue).length,
    mid: visits.filter((v) => v.visit_type === "mid").length,
    post: visits.filter((v) => v.visit_type === "post").length,
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Retrofit Coordination</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Site Visits</h1>
          <p className="mt-1 text-sm text-slate-500">{isAdmin ? "Viewing all engineers' visits" : "Your site visits"}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate("/visits/new?type=mid")} variant="outline" className="field-lg border-slate-300 font-bold" data-testid="new-mid-btn">
            <ClipboardList className="mr-2 h-4 w-4" /> New Mid Visit
          </Button>
          <Button onClick={() => navigate("/visits/new?type=post")} className="field-lg bg-accent font-bold hover:bg-accent/90" data-testid="new-post-btn">
            <Plus className="mr-2 h-4 w-4" /> New Post Visit
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: "Total Visits", value: stats.total, icon: ClipboardCheck, color: "text-slate-900" },
          { label: "Require Remediation", value: stats.remediation, icon: AlertTriangle, color: stats.remediation > 0 ? "text-amber-600" : "text-slate-900", alert: stats.remediation > 0 },
          { label: "Mid Coordination", value: stats.mid, icon: ClipboardList, color: "text-blue-600" },
          { label: "Post Coordination", value: stats.post, icon: ClipboardCheck, color: "text-accent" },
        ].map((s) => (
          <Card key={s.label} className={`p-5 shadow-sm ${s.alert ? "border-2 border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{s.label}</span>
              <s.icon className={`h-5 w-5 ${s.color}`} />
            </div>
            <div className={`mt-3 font-heading text-3xl font-extrabold ${s.color}`}>{s.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by address, postcode or engineer…" value={q} onChange={(e) => setQ(e.target.value)} className="field-lg pl-10" data-testid="visit-search" />
        </div>
        <div className="flex gap-2">
          {[{ k: "all", l: "All" }, { k: "mid", l: "Mid" }, { k: "post", l: "Post" }].map((f) => (
            <Button key={f.k} variant={filter === f.k ? "default" : "outline"} onClick={() => setFilter(f.k)} className={`font-bold ${filter === f.k ? "bg-primary" : "border-slate-300"}`} data-testid={`filter-${f.k}`}>
              {f.l}
            </Button>
          ))}
        </div>
      </div>

      {/* Remediation section */}
      {remediation.length > 0 && (
        <section data-testid="remediation-section">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading text-xl font-bold text-slate-900">Requires Remediation</h2>
            <span className="rounded-full bg-amber-500 px-2.5 py-0.5 text-sm font-bold text-white" data-testid="remediation-count">{remediation.length}</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">Jobs with failed compliance checks or open faults. Resolve and close out before lodgement.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {remediation.map(({ v, issues }) => (
              <VisitCard key={v.id} v={v} issues={issues} onDelete={remove} navigate={navigate} isAdmin={isAdmin} />
            ))}
          </div>
        </section>
      )}

      {/* Cleared / all others */}
      <section>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          {remediation.length > 0 ? "All Clear" : "All Site Visits"}
        </h2>
        {clear.length === 0 ? (
          <Card className="mt-4 flex flex-col items-center justify-center border-dashed border-slate-300 py-16 text-center">
            <ClipboardList className="h-12 w-12 text-slate-300" />
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-700">{filtered.length === 0 ? "No site visits yet" : "No jobs here"}</h3>
            <p className="mt-1 text-sm text-slate-500">{filtered.length === 0 ? "Create your first Mid or Post Coordination visit to get started." : "All matching jobs need remediation."}</p>
          </Card>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {clear.map(({ v, issues }) => (
              <VisitCard key={v.id} v={v} issues={issues} onDelete={remove} navigate={navigate} isAdmin={isAdmin} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
